import React, { useState, useEffect } from 'react';
import { Box, Button, CircularProgress, Typography, TextField, Grid } from '@mui/material';
import { syncData } from '../../../api/index.js';
import useStyles from './styles.js';

const collectionsConfig = [
  { label: 'Sport', value: 'sports' },
  { label: 'Disciplines', value: 'disciplines' },
  { label: 'Catégories', value: 'categoriegenerales' },
  { label: 'Compétitions', value: 'competitions' },
  { label: 'Compétition-disciplines', value: 'competitiondisciplines' },
  { label: 'Epreuves', value: 'epreuves' },
  { label: 'Phases', value: 'phases' },
  { label: 'Sport Events', value: 'sportevents' },
  { label: 'Equipes', value: 'teams' },
  { label: 'Joueurs', value: 'players' },
  { label: 'Lives', value: 'lives' },
  { label: 'Faits de jeux', value: 'gamefacts' },
];

const DataSync = () => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState([]);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState({
    global: {
      versionModel: 'v2',
    },
    collections: [
      'sports',
      'disciplines',
      'categoriegenerales',
      'competitions',
      'competitiondisciplines',
      'epreuves',
      'phases',
      'sportevents',
      'teams',
      'players',
      'lives',
      'gamefacts',
    ],
  });
  const [allData, setAllData] = useState(true);
  const [customFilters, setCustomFilters] = useState({});

  const isEmptyObject = (obj) => {
    return Object.keys(obj).length === 0;
  };

  const handleFilterChange = (event, field, type) => {
    const selectedValue = event.target.value;
    const isChecked = event.target.checked;

    setFilter((prevFilter) => {
      const newFilter = { ...prevFilter };

      // Handle collections filter
      if (field === 'collections') {
        const collectionSet = new Set(newFilter.collections);

        if (isChecked) {
          collectionSet.add(type);
        } else {
          collectionSet.delete(type);
        }

        newFilter.collections = Array.from(collectionSet);
      } else {
        // Handle other global filters like dates
        if (!newFilter.global[field]) {
          newFilter.global[field] = {};
        }

        if (selectedValue) {
          newFilter.global[field][type] = selectedValue;
        } else {
          delete newFilter.global[field][type];
        }

        if (isEmptyObject(newFilter.global[field])) {
          delete newFilter.global[field];
        }
      }

      return newFilter;
    });
  };

  const handleCustomFilterChange = (event, collection, keyOrValue) => {
    const value = event.target.value;

    setCustomFilters((prevFilters) => ({
      ...prevFilters,
      [collection]: {
        ...prevFilters[collection],
        [keyOrValue]: value,
      },
    }));

    setFilter((prevFilter) => {
      const newFilter = { ...prevFilter };
      const collectionName = collection.slice(0, -1); // Remove the last 's' to make it singular

      if (!newFilter[collectionName]) {
        newFilter[collectionName] = {};
      }

      const customFilter = customFilters[collection] || {};
      customFilter[keyOrValue] = value;

      // Ensure we set key-value pair correctly
      const filterKey = customFilter.key;
      const filterValue = [customFilter.value]; // Use an array for the filter value

      if (filterKey && filterValue) {
        newFilter[collectionName] = { [filterKey]: filterValue };
      }

      return newFilter;
    });
  };

  const handleAllDataChange = (event) => {
    const isChecked = event.target.checked;
    setAllData(isChecked);

    if (isChecked) {
      setFilter((prevFilter) => ({
        ...prevFilter,
        collections: collectionsConfig.map((collection) => collection.value),
      }));
    } else {
      setFilter((prevFilter) => ({
        ...prevFilter,
        collections: [],
      }));
    }
  };

  const handleSync = async () => {
    setLoading(true);
    setResponse([]);
    setError(null);

    try {
      const { data } = await syncData({ filter });
      setResponse(data);
      // reset filter
      setFilter({
        global: {
          versionModel: 'v2',
        },
        collections: [
          'sports',
          'disciplines',
          'categoriegenerales',
          'competitions',
          'competitiondisciplines',
          'epreuves',
          'phases',
          'teams',
          'players',
          'sportevents',
          'lives',
          'gamefacts',
        ],
      });
    } catch (error) {
      console.error(error);
      setError(error.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    console.log('Current filter state:', filter);
  }, [filter]);

  return (
    <div className={classes.container}>
      <Typography variant="h5" className={classes.header}>
        Synchroniser les données v2
      </Typography>
      <Typography className={classes.header}>- Envoi au Webhook -</Typography>
      <br />

      {loading && (
        <div className={classes.circularContainer}>
          <CircularProgress />
        </div>
      )}
      {!loading && response.length === 0 && (
        <div>
          {/* DATES FILTER */}
          <Typography>Date de création des données</Typography>
          <Box className={classes.inputWrapper}>
            <Typography sx={{ p: '10px' }}>Du: </Typography>
            <input
              type="datetime-local"
              className={classes.input}
              onChange={(e) => handleFilterChange(e, 'createdDate', 'beginDate')}
            />
          </Box>
          <Box className={classes.inputWrapper}>
            <Typography sx={{ p: '10px' }}>Au: </Typography>
            <input
              type="datetime-local"
              className={classes.input}
              onChange={(e) => handleFilterChange(e, 'createdDate', 'endDate')}
            />
          </Box>
          <br />
          <Typography>Date de dernière mise à jour des données</Typography>
          <Box className={classes.inputWrapper}>
            <Typography sx={{ p: '10px' }}>Du: </Typography>
            <input
              type="datetime-local"
              className={classes.input}
              onChange={(e) => handleFilterChange(e, 'lastModifiedDate', 'beginDate')}
            />
          </Box>
          <Box className={classes.inputWrapper}>
            <Typography sx={{ p: '10px' }}>Au: </Typography>
            <input
              type="datetime-local"
              className={classes.input}
              placeholder="Au:"
              onChange={(e) => handleFilterChange(e, 'lastModifiedDate', 'endDate')}
            />
          </Box>
          <br />

          {/* ALL DATA FILTER */}
          <Typography>Types d'enregistrements à envoyer</Typography>
          <Box className={classes.filterColumn} >
          <input
              type="checkbox"
              className={classes.checkbox}
              checked={allData}
              onChange={handleAllDataChange}
            />
            <Typography sx={{ p: '10px' }}>Tous</Typography>
          </Box>

          {!allData && (
            <>
            <br></br>
            <Typography>Filtrer par collection</Typography>
              {collectionsConfig.map((collection) => (
                <Grid container spacing={2} key={collection.value} className={classes.filterRow}>
                  <Grid item xs={4} className={classes.filterColumn}>
                    <input
                      type="checkbox"
                      className={classes.checkbox}
                      checked={filter.collections.includes(collection.value)}
                      onChange={(e) => handleFilterChange(e, 'collections', collection.value)}
                    />
                    <Typography sx={{ p: '10px' }}>{collection.label}</Typography>
                  </Grid>
                  {filter.collections.includes(collection.value) && (
                    <Grid item xs={8} className={classes.filterColumnRight}>
                      <TextField
                        label="Champ clé"
                        className={classes.customFilterInput}
                        onChange={(e) => handleCustomFilterChange(e, collection.value, 'key')}
                      />
                      <TextField
                        label="Valeur clé"
                        className={classes.customFilterInput}
                        onChange={(e) => handleCustomFilterChange(e, collection.value, 'value')}
                      />
                    </Grid>
                  )}
                </Grid>
              ))}
            </>
          )}

          {/* SENDING BUTTON */}
          <div className={classes.buttonContainer}>
            <Button variant="contained" color="primary" onClick={handleSync}>
              Synchroniser les données
            </Button>
          </div>

          
        </div>
      )}
      {error && <Typography color="error">{error}</Typography>}
          {response.length > 0 && (
            <div className={classes.resultContainer}>
              <Typography variant="h5">Données en cours d'envoi</Typography>
              <Typography className={classes.subtitle_info}>Pour suivre l'avancement, cliquer sur le bouton "suivi de synchro"</Typography>
              <ul className={classes.list}>
                {response.map((res, index) => (
                  <li key={index} className={classes.listItem}>
                    Collection: {res.collection} --- Documents trouvés: {res.found}
                  </li>
                ))}
              </ul>
            </div>
          )}
    </div>
  );
};

export default DataSync;